import { groupBy, sum, sumBy, uniq, uniqWith } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { distanceEquals } from '@paragon/geometry-library';
import { getProductionGroupName } from '@paragon/production-app-utilities';
import { notNullish } from '@paragon/utilities';

import { HeightCache } from '../ComponentGeometryCaches';
import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import { getAggregatedTrussStatisticsByBundleGuid, getBundlesByProductionGroupGuid } from './bundles';
import { getDistanceFormatterSelector } from './getDistanceFormatterSelector';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { getProductionComponentInstancesByProductionGroupGuid } from './getProductionComponentInstancesByProductionGroupGuid';
import { getProjectLayoutModelsByGuid } from './getProjectLayoutModelsByGuid';
import { getGroupedProductionComponentInstancesByBundleGuid } from './productionComponentInstances';
import { getAggregatedTrussStatisticsByProductionGroupGuid } from './productionGroups';
import { sumComponentValueForInstances } from './sumComponentValueInBundle';
import { getLoadedTrussesByAnalysisSetGuid, getTrussMeasurements } from './trusses';

interface BasicRowInformation {
  readonly type: 'project' | 'productionGroup' | 'bundle' | 'groupedProductionComponentInstance';
  readonly rowId: string;
  readonly guid: string;
  readonly name: string;
}

interface ProjectBacklogGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'project';
}

interface ProductionGroupBacklogGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'productionGroup';
  readonly projectGuid: string;
}

interface BundleBacklogGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'bundle';
  readonly productionGroupGuid: string;
}

interface GroupedProductionComponentInstanceBacklogGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'groupedProductionComponentInstance';
  readonly bundleGuid: string;
  readonly analysisSetGuid: string;
}

export type BacklogGridRow =
  | ProjectBacklogGridRow
  | ProductionGroupBacklogGridRow
  | BundleBacklogGridRow
  | GroupedProductionComponentInstanceBacklogGridRow;
export type BacklogGridRowKey = keyof BacklogGridRow;

export function getProjectRowId(projectGuid: string) {
  return `project-${projectGuid}`;
}
export function isProjectRow(rowId: string) {
  return rowId.startsWith('project-');
}
export function getProjectGuidFromRowId(rowId: string) {
  if (!isProjectRow(rowId)) throw new Error('Tried to get the project GUID of a non-project row');
  return rowId.substring('project-'.length);
}
export function getProductionGroupRowId(productionGroupGuid: string) {
  return `productionGroup-${productionGroupGuid}`;
}
export function isProductionGroupRow(rowId: string) {
  return rowId.startsWith('productionGroup-');
}
export function getProductionGroupGuidFromRowId(rowId: string) {
  if (!isProductionGroupRow(rowId))
    throw new Error('Tried to get the production group GUID of a non-production-group row');
  return rowId.substring('productionGroup-'.length);
}
export function getBundleRowId(bundleGuid: string) {
  return `bundle-${bundleGuid}`;
}
export function isBundleRow(rowId: string) {
  return rowId.startsWith('bundle-');
}
export function getBundleGuidFromRowId(rowId: string) {
  if (!isBundleRow(rowId)) throw new Error('Tried to get the bundle GUID of a non-bundle row');
  return rowId.substring('bundle-'.length);
}
export function getGroupedProductionComponentInstanceRowId(bundleGuid: string, analysisSetGuid: string) {
  return `groupedProductionComponentInstance.${bundleGuid}.${analysisSetGuid}`;
}
export function isGroupedProductionComponentInstanceRow(rowId: string) {
  return rowId.startsWith('groupedProductionComponentInstance.');
}

export const getBundlesNotScheduled = createSelector(
  (state: RootState) => state.bundles,

  (bundles) => Object.values(bundles).filter((bundle) => bundle.scheduleGroupGuid === null),
);

export const getBundlesNotScheduledByProductionGroupGuid = createSelector(
  getBundlesNotScheduled,

  (bundlesNotScheduled) => groupBy(bundlesNotScheduled, (bundle) => bundle.productionGroupGuid),
);

export const getBacklogGridRows = createSelector(
  getProjectLayoutModelsByGuid,
  (state: RootState) => state.productionGroups,
  getBundlesNotScheduled,
  getBundlesByProductionGroupGuid,
  getAggregatedTrussStatisticsByBundleGuid,
  getAggregatedTrussStatisticsByProductionGroupGuid,
  getTrussMeasurements,
  getProductionComponentInstancesByProductionGroupGuid,
  getGroupedProductionComponentInstancesByBundleGuid,

  (
    projectLayoutModelsByGuid,
    productionGroups,
    bundlesNotScheduled,
    bundlesByProductionGroupGuid,
    aggregatedTrussStatisticsByBundleGuid,
    aggregatedTrussStatisticsByProductionGroupGuid,
    trussMeasurements,
    productionComponentInstancesByProductionGroupGuid,
    groupedProductionComponentInstancesByBundleGuid,
  ): readonly BacklogGridRow[] => {
    const bundleBacklogGridRows: readonly BundleBacklogGridRow[] = bundlesNotScheduled.map((bundle) => ({
      type: 'bundle',
      rowId: getBundleRowId(bundle.guid),
      guid: bundle.guid,
      name: `Bundle ${bundle.id}`,
      productionGroupGuid: bundle.productionGroupGuid,
      ...aggregatedTrussStatisticsByBundleGuid[bundle.guid],
    }));

    const bundlesNotScheduledGroupedByProductionGroupGuid: {
      [productionGroupGuid: string]: readonly BundleBacklogGridRow[];
    } = {};
    for (const bundle of bundleBacklogGridRows) {
      const { productionGroupGuid } = bundle;
      bundlesNotScheduledGroupedByProductionGroupGuid[productionGroupGuid] = (
        bundlesNotScheduledGroupedByProductionGroupGuid[productionGroupGuid] ?? []
      ).concat([bundle]);
    }

    const productionGroupsWithNoBundles: readonly ProductionGroupBacklogGridRow[] = Object.values(productionGroups)
      .filter((productionGroup) => bundlesByProductionGroupGuid[productionGroup.guid].length === 0)
      .map((productionGroup) => ({
        type: 'productionGroup',
        rowId: getProductionGroupRowId(productionGroup.guid),
        guid: productionGroup.guid,
        name: getProductionGroupName(productionGroup, false),
        projectGuid: productionGroups[productionGroup.guid].projectGuid,
        boardFeet: sumComponentValueForInstances(
          productionComponentInstancesByProductionGroupGuid[productionGroup.guid],
          trussMeasurements,
          (summary) => summary?.boardFeet ?? 0,
        ),
        linearFeet: sumComponentValueForInstances(
          productionComponentInstancesByProductionGroupGuid[productionGroup.guid],
          trussMeasurements,
          (summary) => summary?.linearFeet ?? 0,
        ),
        trussCount: sumComponentValueForInstances(
          productionComponentInstancesByProductionGroupGuid[productionGroup.guid],
          trussMeasurements,
          () => 1,
        ),
        heights: uniqWith(
          productionComponentInstancesByProductionGroupGuid[productionGroup.guid]
            .flatMap((instance) => trussMeasurements[instance.analysisSetGuid]?.heights)
            .filter(notNullish),
          distanceEquals,
        ),
        widths: uniqWith(
          productionComponentInstancesByProductionGroupGuid[productionGroup.guid]
            .flatMap((instance) => trussMeasurements[instance.analysisSetGuid]?.widths)
            .filter(notNullish),
          distanceEquals,
        ),
        thicknesses: uniqWith(
          productionComponentInstancesByProductionGroupGuid[productionGroup.guid]
            .flatMap((instance) => trussMeasurements[instance.analysisSetGuid]?.thicknesses)
            .filter(notNullish),
          distanceEquals,
        ),
      }));
    const productionGroupBacklogGridRows: readonly ProductionGroupBacklogGridRow[] = (
      [] as readonly ProductionGroupBacklogGridRow[]
    )
      .concat(
        Object.entries(bundlesNotScheduledGroupedByProductionGroupGuid).map(
          ([productionGroupGuid, bundlesNotScheduledWithGridProperties]) => ({
            type: 'productionGroup',
            rowId: getProductionGroupRowId(productionGroupGuid),
            guid: productionGroupGuid,
            name: `${getProductionGroupName(productionGroups[productionGroupGuid], false)}${
              bundlesNotScheduledWithGridProperties.length !== bundlesByProductionGroupGuid[productionGroupGuid].length
                ? ' (partial)'
                : ''
            }`,
            projectGuid: productionGroups[productionGroupGuid].projectGuid,
            ...aggregatedTrussStatisticsByProductionGroupGuid[productionGroupGuid],
          }),
        ),
      )
      .concat(productionGroupsWithNoBundles);

    const productionGroupsNotScheduledGroupedByProjectGuid: {
      [projectGuid: string]: readonly BacklogGridRow[];
    } = {};
    for (const productionGroup of productionGroupBacklogGridRows) {
      const { projectGuid } = productionGroup;
      productionGroupsNotScheduledGroupedByProjectGuid[projectGuid] = (
        productionGroupsNotScheduledGroupedByProjectGuid[projectGuid] ?? []
      ).concat([productionGroup]);
    }

    const projectBacklogGridRows: readonly ProjectBacklogGridRow[] = Object.entries(
      productionGroupsNotScheduledGroupedByProjectGuid,
    ).map(([projectGuid, productionGroupsNotScheduledWithGridProperties]) => ({
      type: 'project',
      rowId: getProjectRowId(projectGuid),
      guid: projectGuid,
      name: projectLayoutModelsByGuid[projectGuid]?.name ?? 'unknown',
      boardFeet: sumBy(
        productionGroupsNotScheduledWithGridProperties,
        (productionGroup) => productionGroup.boardFeet ?? 0,
      ),
      linearFeet: sumBy(
        productionGroupsNotScheduledWithGridProperties,
        (productionGroup) => productionGroup.linearFeet ?? 0,
      ),
      trussCount: sumBy(
        productionGroupsNotScheduledWithGridProperties,
        (productionGroup) => productionGroup.trussCount,
      ),
      heights: [],
      widths: [],
      thicknesses: [],
    }));

    const groupedProductionComponentInstanceBacklogGridRows: readonly GroupedProductionComponentInstanceBacklogGridRow[] =
      bundlesNotScheduled.flatMap((bundle) => {
        const groupedProductionComponentInstanceRows: GroupedProductionComponentInstanceBacklogGridRow[] =
          groupedProductionComponentInstancesByBundleGuid[bundle.guid].map(
            ({
              guid,
              analysisSetGuid,
              trussName,
              trussCount,
              boardFeet,
              linearFeet,
              heights,
              widths,
              thicknesses,
            }) => ({
              type: 'groupedProductionComponentInstance',
              rowId: getGroupedProductionComponentInstanceRowId(bundle.guid, analysisSetGuid),
              guid,
              name: trussName,
              bundleGuid: bundle.guid,
              analysisSetGuid,
              trussCount,
              boardFeet,
              linearFeet,
              heights,
              widths,
              thicknesses,
            }),
          );
        return groupedProductionComponentInstanceRows;
      });

    return ([] as BacklogGridRow[])
      .concat(projectBacklogGridRows)
      .concat(productionGroupBacklogGridRows)
      .concat(bundleBacklogGridRows)
      .concat(groupedProductionComponentInstanceBacklogGridRows);
  },
);

export const getProductionGroupsForCheckedRows = createSelector(
  (state: RootState) => state.backlogGrid.checkedRows,
  (state: RootState) => state.bundles,
  (state: RootState) => state.productionGroups,

  (checkedRows, bundles, productionGroups) =>
    uniq(
      checkedRows
        .filter((checkedRow) => isBundleRow(checkedRow))
        .map(getBundleGuidFromRowId)
        .map((bundleGuid) => bundles[bundleGuid].productionGroupGuid),
    ).map((productionGroupGuid) => productionGroups[productionGroupGuid]),
);

export const getBacklogGridCheckedBundleGuids = createSelector(
  (state: RootState) => state.backlogGrid.checkedRows,

  (checkedRows) => checkedRows.filter(isBundleRow).map(getBundleGuidFromRowId),
);

export const getBacklogCheckedRowTotals = createSelector(
  getBacklogGridCheckedBundleGuids,
  getProductionComponentInstancesByBundleGuid,
  getTrussMeasurements,
  getLoadedTrussesByAnalysisSetGuid,
  getDistanceFormatterSelector,

  (
    checkedBundleGuids,
    productionComponentInstancesByBundleGuid,
    trussMeasurements,
    loadedTrussesByAnalysisSetGuid,
    distanceFormatter,
  ) => {
    const checkedTrussCountTotal = sum(
      checkedBundleGuids.map((bundleGuid) => productionComponentInstancesByBundleGuid[bundleGuid].length),
    );
    const checkedBoardFeetTotal = `${Math.round(
      sum(
        checkedBundleGuids.map((bundleGuid) =>
          sumComponentValueForInstances(
            productionComponentInstancesByBundleGuid[bundleGuid],
            trussMeasurements,
            (summary) => summary?.boardFeet ?? 0,
          ),
        ),
      ),
    )} BF`;
    const checkedLinearFeetTotal = `${Math.round(
      sum(
        checkedBundleGuids.map((bundleGuid) =>
          sumComponentValueForInstances(
            productionComponentInstancesByBundleGuid[bundleGuid],
            trussMeasurements,
            (summary) => summary?.linearFeet ?? 0,
          ),
        ),
      ),
    )} LF`;
    const uniqueHeights = uniqWith(
      checkedBundleGuids.flatMap((bundleGuid) =>
        productionComponentInstancesByBundleGuid[bundleGuid]
          .map((instance) =>
            instance.analysisSetGuid in loadedTrussesByAnalysisSetGuid
              ? HeightCache.get(instance.analysisSetGuid, loadedTrussesByAnalysisSetGuid[instance.analysisSetGuid])
              : null,
          )
          .filter(notNullish),
      ),
      distanceEquals,
    );
    const heights =
      uniqueHeights.length === 0 ? null : uniqueHeights.length > 1 ? 'Mixed' : distanceFormatter(uniqueHeights[0]);
    return { checkedTrussCountTotal, checkedBoardFeetTotal, checkedLinearFeetTotal, heights };
  },
);
